// src/components/intentions/RUAHPreferencesSection.tsx
//
// Teachers rank which RUAH subjects they'd like to teach. All teachers get a RUAH,
// so this is about *which* one, not whether. Subjects are pulled live from the
// RUAH grid for the intention period's academic year, deduped by subject name.

import React, { useEffect, useMemo, useState } from 'react';
import { GripVertical, X, ChevronUp, ChevronDown, Sparkles } from 'lucide-react';
import { PreferencesData } from '../../types/intentionPeriods';
import { supabase } from '../../supabase/config';
import { normalizeSubjectName } from '../../utils/subjectName';

interface RUAHPreferencesSectionProps {
  preferences: PreferencesData;
  onChange: (preferences: PreferencesData) => void;
  academicYear: number;
  disabled?: boolean;
}

interface RuahSubjectOption {
  id: string;
  name: string;
}

const RUAHPreferencesSection: React.FC<RUAHPreferencesSectionProps> = ({
  preferences,
  onChange,
  academicYear,
  disabled = false
}) => {
  const [subjects, setSubjects] = useState<RuahSubjectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchRuahSubjects = async () => {
      try {
        setLoading(true);
        // Intention periods open *before* the target year's grid exists, so
        // fall back to the most recent RUAH grid we can find. Subjects are
        // stable year-over-year, so the prior year is a reasonable proxy.
        const { data, error } = await supabase
          .from('grids')
          .select('grid_data')
          .ilike('name', 'RUAH')
          .lte('academic_year', academicYear)
          .order('academic_year', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;

        const classes: Array<{ className?: string }> = data?.grid_data?.classes || [];
        const uniqueNames = new Map<string, string>();
        classes.forEach(c => {
          if (!c.className) return;
          const name = normalizeSubjectName(c.className);
          if (!name) return;
          uniqueNames.set(name, name);
        });

        const options = Array.from(uniqueNames.keys())
          .map(name => ({ id: name, name }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setSubjects(options);
      } catch (err) {
        console.error('Error fetching RUAH subjects:', err);
        setSubjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRuahSubjects();
  }, [academicYear]);

  const selected = preferences.ruahSubjects || [];
  const selectedIds = useMemo(() => new Set(selected.map(s => s.subjectId)), [selected]);

  const filteredSubjects = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return subjects.filter(s => !selectedIds.has(s.id) && s.name.toLowerCase().includes(q));
  }, [subjects, selectedIds, searchTerm]);

  const addSubject = (subject: RuahSubjectOption) => {
    if (selectedIds.has(subject.id)) return;
    onChange({
      ...preferences,
      ruahSubjects: [
        ...selected,
        { subjectId: subject.id, subjectName: subject.name, rank: selected.length + 1 }
      ]
    });
  };

  const removeSubject = (subjectId: string) => {
    const filtered = selected.filter(s => s.subjectId !== subjectId);
    const reranked = filtered.map((s, i) => ({ ...s, rank: i + 1 }));
    onChange({ ...preferences, ruahSubjects: reranked });
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const next = [...selected];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange({ ...preferences, ruahSubjects: next.map((s, i) => ({ ...s, rank: i + 1 })) });
  };

  const moveDown = (index: number) => {
    if (index >= selected.length - 1) return;
    const next = [...selected];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onChange({ ...preferences, ruahSubjects: next.map((s, i) => ({ ...s, rank: i + 1 })) });
  };

  return (
    <div className="bg-white rounded-lg border border-ink-200 p-6">
      <h3 className="text-lg font-semibold text-ink-900 mb-1 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-navy-500" />
        RUAH Subjects
      </h3>
      <p className="text-sm text-ink-500 mb-4">
        Please choose 3 RUAH subjects and order them by preference (your first choice at the top).
      </p>

      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-8 bg-ink-200 rounded w-1/3" />
          <div className="h-32 bg-ink-200 rounded" />
        </div>
      ) : (
        <>
          {/* Selected (ranked) */}
          <div className="space-y-2 mb-3">
            {selected.map((s, index) => (
              <div
                key={s.subjectId}
                className="flex items-center gap-2 p-3 bg-navy-50 rounded-lg border border-navy-200"
              >
                <span className="w-6 h-6 flex items-center justify-center bg-navy-600 text-white rounded-full text-xs font-bold flex-shrink-0 font-mono tabular-nums">
                  {s.rank}
                </span>
                <GripVertical className="w-4 h-4 text-navy-400 flex-shrink-0" />
                <span className="flex-1 font-medium text-ink-900">{s.subjectName}</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => moveUp(index)}
                    disabled={disabled || index === 0}
                    className="p-1 text-ink-400 hover:text-ink-500 disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(index)}
                    disabled={disabled || index === selected.length - 1}
                    className="p-1 text-ink-400 hover:text-ink-500 disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeSubject(s.subjectId)}
                    disabled={disabled}
                    className="p-1 text-danger-solid hover:text-danger-fg"
                    aria-label="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Search */}
          {subjects.length === 0 ? (
            <p className="text-xs text-ink-500 italic">
              No RUAH grid available yet. Ask a curriculum leader to set one up.
            </p>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={disabled}
                placeholder="Search RUAH subjects to add..."
                className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500 text-sm"
              />
              {searchTerm && filteredSubjects.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-ink-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredSubjects.slice(0, 10).map(subject => (
                    <button
                      key={subject.id}
                      type="button"
                      onClick={() => {
                        addSubject(subject);
                        setSearchTerm('');
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-navy-50"
                    >
                      {subject.name}
                    </button>
                  ))}
                </div>
              )}
              {searchTerm && filteredSubjects.length === 0 && (
                <p className="mt-2 text-xs text-ink-500 italic">
                  No RUAH subjects match "{searchTerm}".
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RUAHPreferencesSection;
